import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGeolocation } from "../hooks/useGeolocation";
import { useLocationRefinement } from "../hooks/useLocationRefinement";
import {
  panic,
  PANIC_TAP_COUNT,
  PANIC_TAP_WINDOW_MS,
  PANIC_COUNTDOWN_MS,
} from "../lib/panic";
import { createAlert } from "../lib/api";
import { vibrate } from "../lib/silent";
import { ShieldAlert, X } from "lucide-react";

/**
 * Listens globally for 5 rapid taps. When detected:
 *  1. Vibrates briefly to acknowledge.
 *  2. Shows a discreet cancel ribbon with a 3-second countdown.
 *  3. If not cancelled, dispatches a silent Police alert with the
 *     last-known GPS fix and notifies trusted contacts.
 *  4. Rate-limited: 1 panic alert per 5 minutes.
 *
 * Disabled by default. Toggled in Settings.
 */
export const PanicProvider = ({ children }) => {
  const { coords } = useGeolocation();
  const { arm: armLocationRefinement } = useLocationRefinement(coords);
  const coordsRef = useRef(coords);
  // Keep the latest GPS fix on a ref *inside an effect* so the render
  // body stays pure (react-hooks/refs forbids ref writes during render).
  useEffect(() => { coordsRef.current = coords; }, [coords]);

  const [enabled, setEnabled] = useState(panic.isEnabled());
  const [armed, setArmed] = useState(false);
  const [countdown, setCountdown] = useState(PANIC_COUNTDOWN_MS);

  const tapsRef = useRef([]);
  const armedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => panic.subscribe(setEnabled), []);

  // Define arm / cancel / fireAlert BEFORE the global tap useEffect so
  // its onTap handler does not reference a const sitting in the TDZ.
  // Wrapped in useCallback to keep the purity analyzer happy with the
  // impure Date.now() / setInterval calls inside.
  const fireAlert = useCallback(async () => {
    armedRef.current = false;
    setArmed(false);
    if (!coordsRef.current) {
      vibrate([100, 80, 100]);
      return;
    }
    panic.markTriggered();
    // Iter90 — GPS audit: prefer the rolling best-fix, same as the
    // main SOS flow, so a discreet panic alert isn't pinned to a
    // noisier "latest" sample when a better one landed seconds ago.
    const fix = coordsRef.current.best || coordsRef.current;
    try {
      const alert = await createAlert({
        emergency_type: "Police",
        description: "[ALERTE PANIQUE] Geste discret déclenché",
        coordinates: {
          latitude: fix.latitude,
          longitude: fix.longitude,
          accuracy: fix.accuracy,
          altitude: fix.altitude,
          altitude_accuracy: fix.altitudeAccuracy,
          heading: fix.heading,
          speed: fix.speed,
          timestamp: fix.timestamp,
        },
        notify_contacts: true,
      });
      armLocationRefinement([alert.id], fix.accuracy);
      vibrate(220);
    } catch (_e) {
      vibrate([100, 80, 100]);
    }
  }, [armLocationRefinement]);

  const arm = useCallback(() => {
    if (!panic.canTrigger()) {
      vibrate([40, 60, 40]);
      return;
    }
    vibrate(150);
    armedRef.current = true;
    setArmed(true);
    setCountdown(PANIC_COUNTDOWN_MS);
    const start = Date.now();
    timerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, PANIC_COUNTDOWN_MS - elapsed);
      setCountdown(left);
      if (left === 0) {
        clearInterval(timerRef.current);
        fireAlert();
      }
    }, 100);
  }, [fireAlert]);

  const cancel = useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    clearInterval(timerRef.current);
    armedRef.current = false;
    setArmed(false);
    vibrate(40);
  }, []);

  // Listen for taps globally only when feature is enabled
  useEffect(() => {
    if (!enabled) return undefined;

    const onTap = () => {
      if (armedRef.current) return;
      const now = Date.now();
      tapsRef.current.push(now);
      tapsRef.current = tapsRef.current.filter((t) => now - t <= PANIC_TAP_WINDOW_MS);
      if (tapsRef.current.length >= PANIC_TAP_COUNT) {
        tapsRef.current = [];
        arm();
      }
    };

    document.addEventListener("touchstart", onTap, { passive: true });
    document.addEventListener("click", onTap, true);
    return () => {
      document.removeEventListener("touchstart", onTap);
      document.removeEventListener("click", onTap, true);
    };
  }, [enabled, arm]);

  const pct = Math.round((1 - countdown / PANIC_COUNTDOWN_MS) * 100);

  return (
    <>
      {children}
      {armed && (
        <div
          className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-md z-[200] bg-rescue-ink text-white shadow-2xl"
          data-testid="panic-cancel-overlay"
          // Capture the click here so the 5-tap detector doesn't see this tap
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 shrink-0 text-mali-yellow mt-0.5" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-mali-yellow">
                Geste de panique détecté
              </div>
              <div className="text-sm font-bold mt-0.5">
                Alerte Police envoyée dans {Math.ceil(countdown / 1000)} s
              </div>
              <div className="text-[11px] opacity-80 mt-0.5">
                Touchez « Annuler » si fausse manipulation.
              </div>
            </div>
            <button
              type="button"
              onClick={cancel}
              data-testid="panic-cancel-btn"
              className="shrink-0 bg-white text-rescue-ink px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-mali-yellow transition-colors flex items-center gap-1.5"
            >
              <X className="w-4 h-4" /> Annuler
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-white/20">
            <div
              className="h-full bg-mali-yellow transition-[width] duration-100 ease-linear"
              style={{ width: `${pct}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}
    </>
  );
};
